import {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {ArrowLeftIcon} from 'lucide-react';
import {Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@tinct/web-components';
import {useGetCampaignById} from '@tinct/core-api';
import {useJobStore} from '@tinct/web-layout';
import {BUYER_IMPORT_JOB_TAG} from '@tinct/web-feature-companies';
import {Step6PreviewContent} from '../../funnels/steps/step6-preview/step6-preview';
import type {EditorStepState} from '../../containers/landing-page-studio/landing-page-studio';
import {StudioModeTabs} from '../../containers/landing-page-studio/components/studio-mode-tabs';
import {REMATCHING_LOGOS_REASON, useIsRematchingLogos} from '../../containers/landing-page-studio/hooks';

export type CampaignStudioViewProps = {
  campaignId: string;
  /** Where the back arrow goes. */
  backTo: string;
  /** "Review & publish" target; omit to hide the button (the host has no funnel). */
  publishTo?: string;
};

/**
 * The management-side studio: the same personalize & preview surface as the
 * funnel's Preview & Refine step, framed as edit-in-place for campaigns that
 * finished setup. Hosted by the customer app (workspace routes) and the back
 * office (admin routes); each host provides its own URLs.
 */
export function CampaignStudioView({ campaignId, backTo, publishTo }: CampaignStudioViewProps) {
  const navigate = useNavigate();

  const [editorState, setEditorState] = useState<EditorStepState>({
    isSaving: false,
    isLogoPoolEmpty: false,
    variantsCount: 0,
  });

  const { data: campaign } = useGetCampaignById(
    { id: campaignId, fields: { id: null, name: null } },
    { enabled: !!campaignId },
  );

  // Same gates as the funnel's Continue button, same order: a customer-logo
  // import still running, the variant logos being re-matched, an enabled logo
  // block with nothing to show, or an edit on its way to the server.
  const isImportingBuyers = useJobStore((s) =>
    s.jobs.some((job) => job.tag === BUYER_IMPORT_JOB_TAG && job.status === 'running'),
  );
  const isRematchingLogos = useIsRematchingLogos();
  const publishBlockedReason = isImportingBuyers
    ? 'Importing customer logos… please wait until it finishes.'
    : isRematchingLogos
      ? REMATCHING_LOGOS_REASON
      : editorState.isLogoPoolEmpty
        ? 'Add at least one customer logo, or disable the logo block, to publish.'
        : editorState.isSaving
          ? 'Saving your changes…'
          : null;

  return (
    <div className="flex h-screen flex-col bg-background">
      <header className="flex h-14 shrink-0 items-center justify-between gap-4 border-b border-border px-4">
        <div className="flex min-w-0 items-center gap-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to={backTo} aria-label="Back to campaign">
              <ArrowLeftIcon className="size-4" />
            </Link>
          </Button>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium text-foreground">
              {campaign?.name ?? 'Campaign'}
            </span>
            <span className="text-xs text-muted-foreground">Studio</span>
          </div>
          <StudioModeTabs
            className="ml-2"
            variantsCount={editorState.variantsCount}
          />
        </div>
        {publishTo && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* span keeps the tooltip hoverable while the button is disabled */}
                <span>
                  <Button
                    disabled={publishBlockedReason !== null}
                    onClick={() => navigate(publishTo)}
                  >
                    {editorState.isSaving ? 'Saving…' : 'Review & publish'}
                  </Button>
                </span>
              </TooltipTrigger>
              {publishBlockedReason && <TooltipContent>{publishBlockedReason}</TooltipContent>}
            </Tooltip>
          </TooltipProvider>
        )}
      </header>
      <div className="flex min-h-0 flex-1">
        <Step6PreviewContent campaignId={campaignId} onEditorStateChange={setEditorState} />
      </div>
    </div>
  );
}
